import React from 'react';
import {
    getResponseInitialState,
    getResponseProps,
    useAlert,
    useFetch
} from '../../hooks';
import { RoleTypes, useAuth } from '..';

export type companyTypes = 0 | 1 | 2 | 3 | 'PRISPEK' | 'PRISMA' | 'SPEKTRA';
type activeTypes = 0 | 1;

export interface memberProps {
    Guid: string;
    Name: string;
    Surname: string;
    Email: string;
    Phone?: number | string;
    Contact: number | string;
    Address: string;
    Company: companyTypes;
    Active: activeTypes;
    Role: RoleTypes;
}
export type membersType = Array<memberProps>;

interface MembersContextProps {
    adminMembers: membersType;
    employeeMembers: membersType;
    getMembersResponse: getResponseProps;
    setAdminMembers: React.Dispatch<React.SetStateAction<membersType>>;
    setEmployeeMembers: React.Dispatch<React.SetStateAction<membersType>>;
    getMembers: () => Promise<void>;
}

export const MembersContext = React.createContext<MembersContextProps>({
    adminMembers: [],
    employeeMembers: [],
    getMembersResponse: getResponseInitialState,
    setAdminMembers: () => {},
    setEmployeeMembers: () => {},
    getMembers: async () => {}
});

export const useMembersContext = () => React.useContext(MembersContext);

export const MembersContextProvider: React.FC = ({ children }) => {
    const { session, logout } = useAuth();
    const [adminMembers, setAdminMembers] = React.useState<membersType>([]);
    const [employeeMembers, setEmployeeMembers] = React.useState<membersType>(
        []
    );
    const {
        alert: membersAlert,
        dispatchAlert: dispatchMembersAlert,
        closeAlert: closeMembersAlert
    } = useAlert();
    const [fetchHandler, membersLoading] = useFetch('/dbViewMembers', 'GET');

    const getMembers = React.useCallback(async () => {
        const result = await fetchHandler({
            q: `member_guid=${session.MemberGuid}&&session_guid=${session.SessionGuid}`
        });
        if (result.success) {
            setAdminMembers(
                result.data[0].map((member: memberProps) => {
                    return { ...member, Role: 1 };
                })
            );
            setEmployeeMembers(
                result.data[1].map((member: memberProps) => {
                    return { ...member, Role: 2 };
                })
            );
        } else {
            dispatchMembersAlert({
                type: 'UPDATE_ALERT',
                message: result.msg,
                severity: 'error'
            });
            dispatchMembersAlert({ type: 'OPEN' });
            logout();
        }
    }, [
        dispatchMembersAlert,
        fetchHandler,
        logout,
        session.MemberGuid,
        session.SessionGuid
    ]);

    const getMembersResponse = React.useMemo(() => {
        return {
            loading: membersLoading,
            alert: membersAlert,
            closeAlert: closeMembersAlert
        };
    }, [closeMembersAlert, membersAlert, membersLoading]);

    return (
        <MembersContext.Provider
            value={{
                adminMembers,
                employeeMembers,
                getMembersResponse,
                getMembers,
                setAdminMembers,
                setEmployeeMembers
            }}
        >
            {children}
        </MembersContext.Provider>
    );
};
